import './style.css';

import { type ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { App, ConfigProvider } from 'antd';
import type { ConfigProviderProps } from 'antd/es/config-provider';
import antdLocalePtBR from 'antd/locale/pt_BR';

import { applyEduzzThemeAttribute, type EduzzThemeMode } from '@eduzz/shared';

import { AppBinder } from '.';
import Version from './components/Version';
import { ThemeContext } from './context';
import { useRemoteTheme } from './hooks/useRemoteTheme';

const mediaDark =
  typeof window !== 'undefined' && window?.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;

export interface ThemeProviderProps extends Omit<ConfigProviderProps, 'theme' | 'children' | 'componentSize'> {
  mode?: 'dark' | 'light' | 'system';
  children: ReactNode;
  enableAnimation?: boolean;
}

const ThemeProvider = ({ mode: modeProp = 'light', enableAnimation, children, ...configProps }: ThemeProviderProps) => {
  const [mode, setMode] = useState<EduzzThemeMode>(() => {
    if (modeProp !== 'system') {
      return modeProp;
    }

    return mediaDark?.matches ? 'dark' : 'light';
  });

  const hasManualOverride = useRef(false);

  const antdTheme = useRemoteTheme(mode, enableAnimation);

  const setTheme = useCallback((desiredMode: EduzzThemeMode) => {
    hasManualOverride.current = true;
    setMode(desiredMode);
  }, []);

  const toggle = useCallback(() => {
    hasManualOverride.current = true;
    setMode(current => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  useEffect(() => {
    hasManualOverride.current = false;

    if (modeProp !== 'system') {
      setMode(modeProp ?? 'light');
      return;
    }

    if (!mediaDark) {
      setMode('light');
      return;
    }

    setMode(mediaDark.matches ? 'dark' : 'light');

    const listener = (event: MediaQueryListEvent) => {
      if (hasManualOverride.current) {
        return;
      }

      setMode(event.matches ? 'dark' : 'light');
    };

    mediaDark.addEventListener('change', listener);
    return () => mediaDark.removeEventListener('change', listener);
  }, [modeProp]);

  useEffect(() => {
    applyEduzzThemeAttribute(mode);
  }, [mode]);

  useEffect(() => {
    const themeChangeObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName !== 'data-eduzz-theme') {
          return;
        }

        const attributes = (mutation.target as HTMLElement).attributes;
        const currentTheme = attributes.getNamedItem('data-eduzz-theme')?.value as EduzzThemeMode | undefined;
        const nextTheme = currentTheme ?? 'light';

        setMode(current => (current === nextTheme ? current : nextTheme));
      });
    });

    themeChangeObserver.observe(document.body, { attributes: true });
    return () => themeChangeObserver.disconnect();
  }, []);

  const contextValue = useMemo(
    () => ({
      theme: mode,
      setTheme,
      toggle
    }),
    [mode, setTheme, toggle]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider theme={antdTheme} componentSize='large' locale={antdLocalePtBR} {...configProps}>
        <App>
          <AppBinder />
          <Version />
          <Suspense>{children}</Suspense>
        </App>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
